<template><div><h1 id="_4-ereignisse" tabindex="-1"><a class="header-anchor" href="#_4-ereignisse"><span>4. Ereignisse</span></a></h1>
<h2 id="_4-1-beschreibung" tabindex="-1"><a class="header-anchor" href="#_4-1-beschreibung"><span>4.1. Beschreibung</span></a></h2>
<p>Ereignisse bilden sämtliche Marketingaktivitäten ab, welche in der Unternehmung durchgeführt werden. Dies kann z.B. der Versand eines Kataloges, eine Einladung zu einem Event oder die Fakturierung einer Zusage, etc. sein. Jede im Rahmen dieser Marketingaktivitäten ausgewählte Adresse erhält ein Ereignis als Initialereignis zugewiesen. Für die Produktion von orangen Einzahlungsscheinen ist die Ereignisverknüpfung von zentraler Bedeutung, da funtrade nur mit Hilfe von Initial-Ereignissen eine korrekte automatische Zahlungsverarbeitung vornehmen kann (Stichwort: ESR-Zeile). Sobald die Adresse auf ein bestimmtes Ereignis reagiert, z.B. bei Spenden oft durch eine Zahlung, wird manuell - oder vom System automatisch - eine Response-Ereignis-Verknüpfung erstellt.</p>
<p>Ereignisse sind so zentral, dass sie im Ereignis-Fenster (Adressen &gt; Ereignisse) mit Online-Statistiken aktuell nachgeführt werden, d.h. wenn z.B. Zahlungen auf ein Ereignis eingelesen und verarbeitet werden, kann man dies auf dem Bildschirm online mitverfolgen. Zu jedem Ereignis werden die Anzahl Adressen, welche etwas bekommen haben (Initial-Ereignis), die Reagierer (Response-Ereignis), die Anzahl negativer Reaktionen (Refusé-Ereignis), sowie die evtl. erzielten Spenden- oder Kaufsummen festgehalten.</p>
<p>Für das Marketing ist es somit wichtig, dass jede Adresse, die kontaktiert wird, auch mit einem Initial-Ereignis versehen wird. Zum Vorgehen siehe dazu im Detail <RouteLink to="/manual/customer/selektionen-mailings.html" title="7. Mailings (Segmentierung)">Selektionen, Mailings</RouteLink>.</p>
<p>Aufgrund von Ereignissen können Adressselektionen spezifischer durchgeführt und evtl. Nachfolge-Aktionen effizient gesteuert werden, da das Kundenverhalten bekannt ist.</p>
<p>Um verschiedene Zielgruppen unterscheiden zu können, ermöglicht funtrade das Erfassen von Ereignispositionen. In der Produktion wird beim Verknüpfen der Ereignisse die Position immer ebenfalls gespeichert. Beim Export wird dann diese Position gesucht und in der ESR-Zeile des orangen Einzahlungsscheins kodiert. Bei einem Zahlungseingang kann deshalb der Response auf die richtige Zielgruppe verbucht werden.</p>
<h2 id="_4-2-ereignisse-im-bereich-spenden" tabindex="-1"><a class="header-anchor" href="#_4-2-ereignisse-im-bereich-spenden"><span>4.2. Ereignisse im Bereich Spenden</span></a></h2>
<h3 id="_4-2-1-automatischer-spendeneingang-initialereignis-in-der-esr-zeile" tabindex="-1"><a class="header-anchor" href="#_4-2-1-automatischer-spendeneingang-initialereignis-in-der-esr-zeile"><span>4.2.1. Automatischer Spendeneingang (Initialereignis in der ESR-Zeile)</span></a></h3>
<p>Spenden, mit Einzahlungen per orangen Einzahlungsschein (ESR) oder LSV/DD, werden als Zahlungsfiles mit einem Job in funtrade eingelesen. Die Zahlungen im Zahlungsfile können mit der Referenzzeile klar identifiziert und einem Initialereignis zugeordnet werden. Jede Einzahlung bewirkt in funtrade eine Ereignisverknüpfung bei der entsprechenden Adresse. Die Verknüpfung ist in funtrade sichtbar als Responseereignis.</p>
<h3 id="_4-2-2-manuell-verbuchte-spenden" tabindex="-1"><a class="header-anchor" href="#_4-2-2-manuell-verbuchte-spenden"><span>4.2.2. Manuell verbuchte Spenden</span></a></h3>
<p>Wenn vom Spender kein ESR verwendet wurde, muss die Zahlung manuell verbucht werden. In diesem Falle gibt die buchende Person das Ereignis an.</p>
<p>Beispiele von Ereignisverknüpfungen infolge Spendeneingang (automatisch oder manuell gebucht):</p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">10470 9920 16 RM 24.03.1999 KA1 DM/WV              1999/- S -  96      68.50</span>
<span class="line">10470 9914 01 RM 07.06.1999 Spende aus Warenhandel 1999  (75% net)      2.00</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div></div></div><p>Beim letzteren Beispiel handelt es sich um eine Spende, die ein Kunde auf dem Bestellschein / am Telefon angegeben hat (hier Fr. 2.-). Diese Verknüpfung erfolgt ebenfalls erst bei Zahlungseingang. funtrade wählt entweder das entsprechende Spendenereignis aus der Auftragsarten- oder aus der Medien-Tabelle (siehe unter Bereich Verkauf).</p>
<div class="hint-container tip">
<p class="hint-container-title">Kurz</p>
<p>Spenden (auch Zahlungen infolge Zusagen) werden immer 'vereinnahmt' gebucht und die entsprechenden Ereignisverknüpfungen erfolgen immer erst beim Geldeingang ins System.</p>
</div>
<p>Mit welchem Ereignis eine Verknüpfung stattfinden muss, 'weiss' funtrade aufgrund der im Buchungssatz enthaltenen bzw. der beim Buchen eingegebenen Ereignisnummer.</p>
<h2 id="_4-3-ereignisse-im-bereich-verkauf-response-auf-katalogversand" tabindex="-1"><a class="header-anchor" href="#_4-3-ereignisse-im-bereich-verkauf-response-auf-katalogversand"><span>4.3. Ereignisse im Bereich Verkauf (Response auf Katalogversand)</span></a></h2>
<p>Jeder stattfindende Verkauf - also jede Eingabe eines Auftrages in funtrade - bewirkt ebenfalls eine Ereignisverknüpfung zum Zeitpunkt der Fakturierung des Auftrags. Wird ein Auftrag als Vorauszahlungsauftrag erfasst, findet die Ereignisverknüpfung erst bei der Fakturierung NACH Eingang des Vorauszahlungsbetrags statt, bei der effektiv eine Lieferung ausgelöst wird. funtrade verwendet entweder das Ereignis, welches in der Tabelle Auftragsarten definiert ist oder jenes in der Medientabelle. Priorität hat dabei die Medientabelle. In der Tabelle Auftragsarten ist die Eingabe eines Verkaufs- und eines Spendenereignisses zwingend, in der Medientabelle jedoch nicht. Wenn zum Medium keine Ereignisnummern erfasst wurden ODER wenn in der Auftragsposition kein Medium erfasst wurde, wählt funtrade für die Verknüpfung das Ereignis, welches in der Tabelle Auftragsarten angegeben ist. Werden in einem einzigen Auftrag mehrere Medien erfasst, weil der Kunde aus verschiedenen Katalogen Waren bestellt,</p>
<ul>
<li>
<p>werden die Umsätze pro Medium korrekt erfasst,</p>
</li>
<li>
<p>erstellt das System für jedes vorkommende Ereignis eine Verknüpfung.</p>
</li>
<li>
<p>Porto und Versandspesen werden mit der umsatzgrössten Auftragsposition verbunden und so im Ereignisumsatz berücksichtigt.</p>
</li>
</ul>
<h3 id="_4-3-1-beispiele" tabindex="-1"><a class="header-anchor" href="#_4-3-1-beispiele"><span>4.3.1. Beispiele</span></a></h3>
<p>Im Herbst 99 wurde ein Mail verschickt, das in funtrade als Ereignis 9920 definiert ist.</p>
<p>In der Auftragsarten-Tabelle sind 9920 als Verkaufsereignis und 9940 als Spendenereignis definiert. Im Mail wurden 2 Kataloge verschickt, einer wird als Medium 13, der andere als Medium 14 bezeichnet. In der Medientabelle steht Verkaufsereignis 9930 und Spendenereignis 9950. Diese Situation lässt sich so kurz darstellen:</p>
<p><strong>Parametrisierung (Definitionen) in funtrade:</strong></p>
<table>
<thead>
<tr>
<th>Auftragsart 'dm'<br>Verkaufsereignis</th>
<th>Spendenereignis</th>
<th>Medium 13<br>Verkaufsereignis</th>
<th>Spendenereignis</th>
</tr>
</thead>
<tbody>
<tr>
<td>9920</td>
<td>9940</td>
<td>9930</td>
<td>9950</td>
</tr>
</tbody>
</table>
<p>Verkauft wird Artikel 1234.13 zu Fr. 50.-. (Die letzten beiden Ziffern bezeichnen das Verkaufsmedium; in der Regel wird die Mediennummer hinter der Artikelnummer gedruckt, getrennt durch einen Punkt.) Der Kunde rundet mit einer Spende von Fr. 10.- auf Fr. 60.- auf. Erfasst wird ein Auftrag mit nur einer Position und im Auftragskopf wird Fr. 10.- als Spende eingetragen.</p>
<p>In der Auftragsposition wird erfasst: 1234.13 (Artikelnummer.Medium). Folgende Ereignisverknüpfung entsteht bei der Adresse des Auftraggebers, zum Zeitpunkt der Fakturierung:</p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">10470 9930 16 RM 24.11.1999 KA2 DM                                     50.00</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div></div></div><p>Später, wenn der Auftrag bezahlt wird (hier am 3.12.1999), findet folgende Ereignisverknüpfung statt, da Spenden immer vereinnahmt, d.h. bei Zahlungseingang verbucht werden:</p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">10470 9950 01 RM 03.12.1999 Spende aus Warenhandel 1999                10.00</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div></div></div><p>Zur Verdeutlichung seien mit diesem Auftragsbeispiel weitere mögliche Situationen in funtrade durchgespielt:</p>
<table>
<thead>
<tr>
<th>Eingabe bei Auftragserfassung</th>
<th></th>
<th></th>
<th></th>
<th></th>
<th></th>
<th>resultierende Verknüpfung</th>
<th></th>
</tr>
</thead>
<tbody>
<tr>
<td>in Auftragsarten-Tabelle</td>
<td></td>
<td>in Medientabelle</td>
<td></td>
<td>im Auftrag</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>Artikel</td>
<td>Spende</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
</tr>
<tr>
<td>9920</td>
<td>9940</td>
<td>9930</td>
<td>9950</td>
<td>1234.13*)</td>
<td>ja</td>
<td>9930</td>
<td>9950</td>
</tr>
</tbody>
</table>
<p>*) Als Medium wurde 13 eingegeben. Da die in der Medientabelle angegeben Ereignisse Priorität haben, wählt funtrade 9930 bzw. 9950 für die Verknüpfungen.</p>
<table>
<thead>
<tr>
<th>Eingabe bei Auftragserfassung</th>
<th></th>
<th></th>
<th></th>
<th></th>
<th></th>
<th>resultierende Verknüpfung</th>
<th></th>
</tr>
</thead>
<tbody>
<tr>
<td>in Auftragsarten-Tabelle</td>
<td></td>
<td>in Medientabelle</td>
<td></td>
<td>im Auftrag</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>Artikel</td>
<td>Spende</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
</tr>
<tr>
<td>9920</td>
<td>9940</td>
<td>9930</td>
<td>9950</td>
<td>1234.00*)</td>
<td>ja</td>
<td>9920</td>
<td>9940</td>
</tr>
</tbody>
</table>
<p>*) Es wurde kein Medium hinter der Artikelnummer eingegeben. Deshalb wählt funtrade die in der Auftragsarten-Tabelle angegebenen Ereignisse 9920 bzw. 9940.</p>
<table>
<thead>
<tr>
<th>Eingabe bei Auftragserfassung</th>
<th></th>
<th></th>
<th></th>
<th></th>
<th></th>
<th>resultierende Verknüpfung</th>
<th></th>
</tr>
</thead>
<tbody>
<tr>
<td>in Auftragsarten-Tabelle</td>
<td></td>
<td>in Medientabelle</td>
<td></td>
<td>im Auftrag</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>Artikel</td>
<td>Spende</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
</tr>
<tr>
<td>9920</td>
<td>9940</td>
<td>leer</td>
<td>leer</td>
<td>1234.13</td>
<td>ja</td>
<td>9920</td>
<td>9940</td>
</tr>
</tbody>
</table>
<table>
<thead>
<tr>
<th>Eingabe bei Auftragserfassung</th>
<th></th>
<th></th>
<th></th>
<th></th>
<th></th>
<th>resultierende Verknüpfung</th>
<th></th>
</tr>
</thead>
<tbody>
<tr>
<td>in Auftragsarten-Tabelle</td>
<td></td>
<td>in Medientabelle</td>
<td></td>
<td>im Auftrag</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>Artikel</td>
<td>Spende</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
</tr>
<tr>
<td>9920</td>
<td>9940</td>
<td>leer</td>
<td>leer</td>
<td>1234.00</td>
<td>nein</td>
<td>9920</td>
<td>keine</td>
</tr>
</tbody>
</table>
<table>
<thead>
<tr>
<th>Eingabe bei Auftragserfassung</th>
<th></th>
<th></th>
<th></th>
<th></th>
<th></th>
<th>resultierende Verknüpfung</th>
<th></th>
</tr>
</thead>
<tbody>
<tr>
<td>in Auftragsarten-Tabelle</td>
<td></td>
<td>in Medientabelle</td>
<td></td>
<td>im Auftrag</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>Artikel</td>
<td>Spende</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
</tr>
<tr>
<td>9920</td>
<td>9940</td>
<td>9930</td>
<td>leer</td>
<td>1234.13</td>
<td>ja</td>
<td>9930</td>
<td>9940</td>
</tr>
</tbody>
</table>
<table>
<thead>
<tr>
<th>Eingabe bei Auftragserfassung</th>
<th></th>
<th></th>
<th></th>
<th></th>
<th></th>
<th>resultierende Verknüpfung</th>
<th></th>
</tr>
</thead>
<tbody>
<tr>
<td>in Auftragsarten-Tabelle</td>
<td></td>
<td>in Medientabelle</td>
<td></td>
<td>im Auftrag</td>
<td></td>
<td></td>
<td></td>
</tr>
<tr>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
<td>Artikel</td>
<td>Spende</td>
<td>VerkaufsE</td>
<td>SpendenE</td>
</tr>
<tr>
<td>9920</td>
<td>9940</td>
<td>leer</td>
<td>9950</td>
<td>1234.13</td>
<td>ja</td>
<td>9920</td>
<td>9950</td>
</tr>
</tbody>
</table>
<h2 id="_4-4-ereignispositionen" tabindex="-1"><a class="header-anchor" href="#_4-4-ereignispositionen"><span>4.4. Ereignispositionen</span></a></h2>
<p>Zu jedem Ereignis lassen sich mehrere (max. 99) Ereignispositionen definieren. Die Ereignisposition steht für eine Untergruppe der Adressaten, für eine Zielgruppe in einem Mail. Welche Ereignisnummer (4-stellig) funtrade für eine Verknüpfung wählt, wurde in den obigen Abschnitten erklärt. Welche Ereignisposition (5. und 6. Stelle) wählt nun funtrade bei der Verknüpfung mit einer Adresse?</p>
<h3 id="_4-4-1-spendeneingang-auf-ein-sog-reines-spendenereignis" tabindex="-1"><a class="header-anchor" href="#_4-4-1-spendeneingang-auf-ein-sog-reines-spendenereignis"><span>4.4.1. Spendeneingang auf ein sog. reines Spendenereignis</span></a></h3>
<p>Wenn z.B. von Adresse mit Nummer 234234 eine Spende auf Ereignis 4711 eingeht, sucht funtrade ein Initialereignis bei Adresse 234234. Nehmen wir an, dass zur Adresse 234234 ein Initialereignis mit der Nummer 4711.05 existiert (weil die Adresse zur Untergruppe 05 gehört). In diesem Fall wird der Zahlungseingang (Response) als R 4711.05 gebucht. Wenn kein Initialereignis bei der Adresse des Zahlenden gefunden wird, erfolgt die Verknüpfung mit R 4711.00.</p>
<div class="hint-container tip">
<p class="hint-container-title">Kurz</p>
<p>funtrade sucht bei der Adresse ein Initialereignis und übernimmt dessen Positionsnummer. Wenn kein I-Ereignis vorhanden ist, wählt funtrade die Positionsnummer 00 für das entsprechende R-Ereignis.</p>
</div>
<h3 id="_4-4-2-spendeneingang-auf-ein-spendenereignis-zum-auftrag" tabindex="-1"><a class="header-anchor" href="#_4-4-2-spendeneingang-auf-ein-spendenereignis-zum-auftrag"><span>4.4.2. Spendeneingang auf ein 'Spendenereignis zum Auftrag'</span></a></h3>
<p>funtrade sucht <em>kein</em> Initialereignis bei der Adresse. Wenn in der Medien- oder Auftragsartentabelle ein 6-stelliges Spendenereignis definiert wurde (z.B. 5600.07) wählt funtrade die Positionsnummer 07. Wenn in der Medien- oder Auftragsartentabelle nur ein 4-stelliges Spendenereignis definiert wurde (z.B. 5600), wird 00 als Positionsnummer gewählt.</p>
<h3 id="_4-4-3-verkaufsereignis" tabindex="-1"><a class="header-anchor" href="#_4-4-3-verkaufsereignis"><span>4.4.3. Verkaufsereignis</span></a></h3>
<p>Unabhängig davon, ob in der Tabelle Medien oder Auftragsarten ein 4-stelliges oder 6-stelliges Verkaufsereignis definiert wurde, sucht funtrade immer ein entsprechendes Initialereignis bei der Adresse des Auftraggebers. Nur wenn dort kein Initialereignis vorhanden ist, wird die Positionsnummer des 6-stelligen Verkaufsereignisses in den Tabellen Medien oder Auftragsarten berücksichtigt.</p>
<h4 id="_4-4-3-1-beispiele" tabindex="-1"><a class="header-anchor" href="#_4-4-3-1-beispiele"><span>4.4.3.1. Beispiele</span></a></h4>
<div class="hint-container tip">
<p class="hint-container-title">Kurz</p>
<p>Bei der Verknüpfung von Verkaufsereignissen wird — wie bei normalen Spendenereignissen — immer zuerst bei der Adresse nach einem Initialereignis gesucht und dessen Positionsnummer übernommen. Wenn kein entsprechendes Initialereignis bei der Adresse gefunden wird, sucht funtrade eine Positionsnummer in der Medien- und dann in der Auftragsarten-Tabelle und übernimmt diese in die Responseereignis-Verknüpfung. Erst wenn auch dort keine Positionsnummer (5. und 6. Stelle) gefunden wird, nimmt funtrade die Positionsnummer 00.</p>
</div>
<table>
<thead>
<tr>
<th></th>
<th>Initial-Ereignis bei Adresse</th>
<th>Verkaufsereignis bei Auftragsart</th>
<th>Verkaufsereignis bei Medium</th>
<th>bewirkt bei Adresse Verknüpfung mit Responseereignis</th>
</tr>
</thead>
<tbody>
<tr>
<td>1</td>
<td>5600.13</td>
<td>5600.00</td>
<td>5600.15</td>
<td>5600.13</td>
</tr>
<tr>
<td>2</td>
<td>nicht vorhanden</td>
<td>5600.00</td>
<td>5600.00</td>
<td>5600.00</td>
</tr>
<tr>
<td>3</td>
<td>nicht vorhanden</td>
<td>5600.88</td>
<td>leer</td>
<td>5600.88</td>
</tr>
<tr>
<td>4</td>
<td>5600.13</td>
<td>5600.88</td>
<td>leer</td>
<td>5600.13</td>
</tr>
<tr>
<td>5</td>
<td>5600.13</td>
<td>5600.88</td>
<td>5600.88</td>
<td>5600.13</td>
</tr>
<tr>
<td>6</td>
<td>nicht vorhanden</td>
<td>5600.55</td>
<td>5600.77</td>
<td>5600.77</td>
</tr>
</tbody>
</table>
<h2 id="_4-5-systemvorbereitung-parametrisierung-der-ereignisverknupfung" tabindex="-1"><a class="header-anchor" href="#_4-5-systemvorbereitung-parametrisierung-der-ereignisverknupfung"><span>4.5. Systemvorbereitung: Parametrisierung der Ereignisverknüpfung</span></a></h2>
<p>Damit alle Geldflüsse und Verkaufsvorgänge in funtrade in den Online-Statistiken und Auswertungen korrekt abgebildet werden, muss das System vorbereitet bzw. parametrisiert werden:</p>
<h3 id="_4-5-1-einmalige-vorarbeiten" tabindex="-1"><a class="header-anchor" href="#_4-5-1-einmalige-vorarbeiten"><span>4.5.1. Einmalige Vorarbeiten</span></a></h3>
<p>Die Kontierung ist Thema eines separaten Kapitels der Dokumentation; hier wird das Thema nur gestreift, um auf die Zusammenhänge zwischen Ereignisverknüpfung und Kontierung hinzuweisen.</p>
<p>Jeder Zahlungseingang bewirkt eine Ereignisverknüpfung und (mindestens) eine Einzelbuchung. Auch die Kontierung selbst ist parametrisierbar - siehe u.a. Tabelle 'Kontierungsquellen'. Für Spenden werden in der Regel die zum entsprechenden Ereignis definierten Konto, Kostenstelle und Kostenträger gewählt, d.h. in die Einzelbuchung übernommen (siehe Ereignistabelle).</p>
<p>Bei Zahlungseingängen infolge Auftrag (Warenverkehr) erlaubt funtrade die Wahl der Kontierungsquelle, nämlich Auftragsart, Medium, Artikelgruppe, Artikel oder Productmanager. In einem Warenhandelsereignis kann also keine Kto/Kst/Ktr-Kombinationen eingegeben werden.</p>
<p>Wo Ereignisse als Quelle der Konto-, Kostenstellen- oder Kostenträger-Information dienen, ist die Parametrisierung der Ereignisverknüpfung auch für die Buchhaltung - für die korrekte Verbuchung von Forderungen (OP) und Zahlungen - von entscheidender Bedeutung.</p>
<dl>
<dt>Schritt 1:</dt>
<dd>Konten, Kostenstellen, Kostenträger in entsprechenden Buchhaltungstabellen eintragen</dd>
<dt>Schritt 2:</dt>
<dd>Kontierungsquelle in Tabelle Kontierungsquellen definieren</dd>
<dt>Schritt 3:</dt>
<dd>Kontierungs-Stammdaten korrekt erfassen</dd>
</dl>
<p>Siehe dazu im Detail Konti</p>
<h3 id="_4-5-2-wiederkehrende-vorarbeiten" tabindex="-1"><a class="header-anchor" href="#_4-5-2-wiederkehrende-vorarbeiten"><span>4.5.2. Wiederkehrende Vorarbeiten</span></a></h3>
<dl>
<dt>Schritt 1:</dt>
<dd>Ereignis für Verkauf / Spende in Ereignistabelle eintragen</dd>
<dt>Schritt 2:</dt>
<dd>Ereignis für Spenden aus Verkaufsaktion in Ereignistabelle eintragen</dd>
<dt>Schritt 3:</dt>
<dd>Medien in Medientabelle eintragen</dd>
<dt>Schritt 4:</dt>
<dd>Auftragsart in Auftragsarten-Tabelle eintragen bzw. einen bestehenden Eintrag anpassen.</dd>
</dl>
<pre><code>Um ein Ereignis vollständig richtig erfassen zu können, sind folgende Steuertabellen vorgängig zu beachten:
</code></pre>
<p><RouteLink to="/referenz/refs/ft.refdata.ereigsit.html" title="Ereignissituationen">Ereignissituationen</RouteLink></p>
<h3 id="_4-5-3-ereignis-status" tabindex="-1"><a class="header-anchor" href="#_4-5-3-ereignis-status"><span>4.5.3. Ereignis-Status</span></a></h3>
<p>Pfad: <code v-pre>Systemverwaltung → Systemtabellen → Ereignis-Status</code></p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">gehezu suchen erfassen ändern löschen druck</span>
<span class="line">+------------------------------- Ereignisstatus -------------------------------+</span>
<span class="line">|Status Typ Kat Bezeichnung                                               Basis|</span>
<span class="line">|------ --- --- --------------------------------------------------------- -----|</span>
<span class="line">|I      I       Initialereignis allgemein                                 ja   |</span>
<span class="line">|IC     I   C   Verdankung                                                nein |</span>
<span class="line">|IM     I   M   Initialereignis und Mailing                               ja   |</span>
<span class="line">|R      R       Responseereignis allgemein                                ja   |</span>
<span class="line">|RM     R   M   Responseereignis auf Mailing                              ja   |</span>
<span class="line">|XA     X   A   Absagen (für RDI-Ereignisse)                              ja   |</span>
<span class="line">|XE     X   E   temporär gesperrt                                         ja   |</span>
<span class="line">|XK     X   K   Keine Zustellung im Bereich Z,S odrer K (Selektiv)        ja   |</span>
<span class="line">|XL     X   L   zum Löschen markiert                                      ja   |</span>
<span class="line">|XN     X   N   stiller SpenderIn - will NIE Post erhalten                ja   |</span>
<span class="line">|XT     X   T   gestorben, oder Firma erloschen                           ja   |</span>
<span class="line">|XU     X   U   unzustellbar, unbekannt                                   ja   |</span>
<span class="line">|XV     X   V   Verweigert, Adresse wird sofort gesperrt                  ja   |</span>
<span class="line">|XX     X   X   Refusé aus unbekannten Gründen (nach 3x gesperrt)         ja   |</span>
<span class="line">+------------------------------------------------------------------------------+</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><p>Die wichtigsten Ereignis-Stati welche aus Typ und Kategorie bestehen sind hier detailliert aufgeführt: Ereignisstatus</p>
<h2 id="_4-6-ereignis-erfassen" tabindex="-1"><a class="header-anchor" href="#_4-6-ereignis-erfassen"><span>4.6. Ereignis erfassen</span></a></h2>
<p>Nachdem in den vorgängig beschriebenen Tabellen alle Werte hinterlegt sind, kann das Ereignis erfasst werden.</p>
<p>Das Ereignisfenster besteht aus zwei Teilen: Ereigniskopf (oberer Teil) und Ereignispositionen (unterer Teil). Mit Ereignispositionen können die selektierten Adressen in verschiedenen Untergruppierungen (Zielgruppen) geführt werden. Das ist vor allem dann wichtig, wenn unterschiedliche Beilagen versendet oder einzelne Gruppen sprachgetrennt angesprochen werden sollen. Die Ereignisposition 00 wird beim Erfassen als Default-Wert eingetragen.</p>
<p>Pfad: <code v-pre>Adressen → Ereignisse</code></p>
<p>Siehe auch <RouteLink to="/referenz/refs/ft.refdata.ereigkopf.html" title="Ereignisse">Ereignis-Kopf</RouteLink></p>
<p>Siehe auch <RouteLink to="/referenz/refs/ft.refdata.ereigpos.html" title="Ereignispositionen">Ereignispositionen</RouteLink></p>
<h2 id="_4-7-ereignisse-verknupfen" tabindex="-1"><a class="header-anchor" href="#_4-7-ereignisse-verknupfen"><span>4.7. Ereignisse verknüpfen</span></a></h2>
<h3 id="_4-7-1-manuelle-verknupfung" tabindex="-1"><a class="header-anchor" href="#_4-7-1-manuelle-verknupfung"><span>4.7.1. Manuelle Verknüpfung</span></a></h3>
<p>Jederzeit kann eine einzelne Adresse mit einem Ereignis verknüpft werden. Manuell erfasst werden meistens Refusé-Ereignisse, weshalb standardmässig beim Ereignisstatus ein &quot;X&quot; vorgeschlagen wird. Der Vorschlag kann überschrieben werden. Manuelle Verknüpfungen können z.B. auch benutzt werden, um den Anruf eines Kunden zu erfassen, der allgemeine Unterlagen zur Organisation haben will. Damit wird im System der Versand von Unterlagen (evtl. inkl. einem Spendeneinzahlungsschein) festgehalten. Meist werden für diesen Zweck aber automatische Verfahren bei den Kunden eingesetzt.</p>
<p>Pfad: <code v-pre>Adressen → Adressen → Ereignisse</code></p>
<p>Hier sind alle Adressreignisse aufgelistet, die zu dieser Adresse und relevanten Adressverknüpfungen (z.B. Haushalten) gehören. Siehe dazu im Detail <RouteLink to="/referenz/refs/ft.refdata.ereigknuepfman.html" title="Ereignisse zu Person">Manuelle Ereignisverknüpfung</RouteLink></p>
<h2 id="_4-8-automatische-verknupfung" tabindex="-1"><a class="header-anchor" href="#_4-8-automatische-verknupfung"><span>4.8. Automatische Verknüpfung</span></a></h2>
<h3 id="_4-8-1-verknupfung-uber-adressfilter" tabindex="-1"><a class="header-anchor" href="#_4-8-1-verknupfung-uber-adressfilter"><span>4.8.1. Verknüpfung über Adressfilter</span></a></h3>
<p>Um automatische Verknüpfungen über Adressfilter zu realisieren, ist ein zweistufiges Vorgehen notwendig. Zuerst werden die Adressfilter mit den gewünschten Ereignissen / Merkmalen verknüpft. In einem zweiten Schritt werden diese Verknüpfungen für alle Adressen, welche in diesen Adressfiltern vorhanden sind, effektiv ausgeführt, so dass anschliessend bei jeder Adresse das Ereignis im Adressfenster und im Ereignisfenster erscheint. Das im Folgenden beschriebene Vorgehen gilt für Adressfilter, die bereits markiert worden sind. Ist der Adressfilter noch nicht markiert, wird - nachdem die Verknüpfung definiert ist - die Markierung automatisch ausgeführt.</p>
<p>Siehe im Detail <RouteLink to="/referenz/refs/ft.refdata.adrfilter.html#Filter" title="Beschreibung">Adressfilter</RouteLink>.</p>
<h3 id="_4-8-2-verknupfung-uber-auftragsart" tabindex="-1"><a class="header-anchor" href="#_4-8-2-verknupfung-uber-auftragsart"><span>4.8.2. Verknüpfung über Auftragsart</span></a></h3>
<p>Kauft ein Kunde Ware, wird dieses Ereignis als Response vom System automatisch erfasst. Damit diese Ereignisverknüpfung im Hintergrund ablaufen kann, muss ein entsprechender Eintrag in der Auftragsart oder im Medium vorhanden sein.</p>
<h3 id="_4-8-3-verknupfung-uber-medium" tabindex="-1"><a class="header-anchor" href="#_4-8-3-verknupfung-uber-medium"><span>4.8.3. Verknüpfung über Medium</span></a></h3>
<p>Kauft ein Kunde Ware, wird dieses Ereignis als Response vom System automatisch erfasst. Damit diese Ereignisverknüpfung im Hintergrund ablaufen kann, muss ein entsprechender Eintrag in der Auftragsart oder im Medium vorhanden sein.</p>
<h2 id="_4-9-beispiele-von-ereignisverknupfungen" tabindex="-1"><a class="header-anchor" href="#_4-9-beispiele-von-ereignisverknupfungen"><span>4.9. Beispiele von Ereignisverknüpfungen</span></a></h2>
<p>Die Parametrisierung eines Systems, das automatische Buchungen und zugleich automatische Nachführung von Online-Statistiken erlaubt, ist immer eine komplexe Angelegenheit. Deshalb soll sie zusätzlich mit Beispielen beschrieben werden.</p>
<h3 id="_4-9-1-beispiel-a" tabindex="-1"><a class="header-anchor" href="#_4-9-1-beispiel-a"><span>4.9.1. Beispiel A</span></a></h3>
<p>Mailing mit Spendenaufruf an 3 Adressgruppen in 3 Sprachen. Daraus ergibt sich folgender</p>
<h3 id="_4-9-1-1-eintrag-in-der-ereignistabelle-reines-spendenereignis" tabindex="-1"><a class="header-anchor" href="#_4-9-1-1-eintrag-in-der-ereignistabelle-reines-spendenereignis"><span>4.9.1.1. Eintrag in der Ereignistabelle (reines Spendenereignis)</span></a></h3>
<p>Pfad: <code v-pre>Adressen → Ereignisse</code></p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">+------------------------------- Ereignis-Kopf --------------------------------+</span>
<span class="line">|Ereignis.: 4567 Spendenaufruf für Projekt Xyz                                 |</span>
<span class="line">|Situation: RSS  Spende/Aktion                                                 |</span>
<span class="line">|Proj/Code: Spen   /K     Alle SpendenMails 1999                               |</span>
<span class="line">|aktiv......: ja          Mand./MWST: X/          Initialanzahl: 1             |</span>
<span class="line">|Zusage/Jahr:    /        Konto.....: 3010        Refusé-Anzahl:               |</span>
<span class="line">|Umsatz/Akq.: SPE         Kostenst..: 70100       Responsanzahl:               |</span>
<span class="line">|Initialdat.: 15.04.1999  Kostentr..:             Respons in %.:               |</span>
<span class="line">|gilt bis...:             ESR-Nummer: 010232833   Responsewert.:               |</span>
<span class="line">+------------------------------------------------------------------------------+</span>
<span class="line">+----------------------------- Ereignis-Position ------------------------------+</span>
<span class="line">|EP Bezeichnung                    FDb Initial Refusé Respons  Res% Responswert|</span>
<span class="line">|00 ---                                      0      0       0              0.00|</span>
<span class="line">|01 beste Spender    d                   80000      0       0  0.00        0.00|</span>
<span class="line">|02 gute Spender     d                  160000      0       0  0.00        0.00|</span>
<span class="line">|03 schwache Spender d                  140000      0       0  0.00        0.00|</span>
<span class="line">|04 beste Spender    f                   20000      0       0  0.00        0.00|</span>
<span class="line">etc.</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><p>Die ab Datenträger eingelesenen Zahlungen werden als Response auf Ereignis 4567 gewertet. Das System kontrolliert dabei, ob zur Adresse eine Initialereignis-Verknüpfung besteht. Wenn ja, wird die Ereignisposition übernommen, so dass der Response auf die korrekte Zielgruppe im Ereignis verbucht wird. Zahlungseingänge von Adressaten, die das Mail nicht erhalten haben (also keine Initialereignis-Verknüpfung bei ihrer Adresse haben), erhalten bei der automatischen Verbuchung eine Responseereignis-Verknüpfung mit Nr. 4567.00 (undefinierte Positionsnummer). Bei der manuellen Verbuchung von Zahlungseingängen für Projekt Xyz ist als Ereignisnummer 4567 anzugeben. Es kann vorgesehen werden, dass manuelle Buchungen (sofern bei der Adresse kein Initialereignis vorhanden ist), auf eine separate Ereignisposition verbucht werden sollen. In diesem Fall ist zusätzlich zur 4-stelligen Ereignisnummer die entsprechende 2-stellige Ereignispositionsnummer zu erfassen. funtrade schlägt in den manuellen Buchungen lediglich die Ereignisposition 00 vor.</p>
<h3 id="_4-9-2-beispiel-b" tabindex="-1"><a class="header-anchor" href="#_4-9-2-beispiel-b"><span>4.9.2. Beispiel B</span></a></h3>
<p>Mailing mit 1 Katalog und 1 Flyer an 3 Adressgruppen in 3 Sprachen. Als Ereignisnummer wird 9876 gewählt. Auf dem Bestellschein kann der Käufer eine Spende versprechen. Spendenanteile der Aufträge sollen auf Ereignisnummer 7777.14 gebucht werden.Weil der Verkaufserfolg auch nach Verkaufsmedien ausgewertet werden soll, sind zwei Medien zu definieren. Der Katalog erhält die Mediennummer 24, der Flyer die Mediennummer 25.</p>
<p>Rahmenbedingung: Als Kontierungsquelle gilt der Artikelstamm.</p>
<h4 id="_4-9-2-1-eintrag-in-der-ereignistabelle-verkaufsereignis" tabindex="-1"><a class="header-anchor" href="#_4-9-2-1-eintrag-in-der-ereignistabelle-verkaufsereignis"><span>4.9.2.1. Eintrag in der Ereignistabelle (Verkaufsereignis)</span></a></h4>
<p>Für die Zuordnung von Reaktionen (Aufträgen) und Lenkung der Erträge aus Verkäufen ist folgendes Ereignis zu erfassen:</p>
<p>Pfad: `Adressen → Ereignisse``</p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">+-------------------------------- Ereignis-Kopf -------------------------------+</span>
<span class="line">|Ereignis.: 9876 Verkaufsaktion Abc                                            |</span>
<span class="line">|Situation: RWV  Katalogversand, Warenverkauf                                  |</span>
<span class="line">|Proj/Code: Kau    /K     Alle Verkaufsaktionen 1999                           |</span>
<span class="line">|aktiv......: ja          Mand./MWST: X/          Initialanzahl: 500'000       |</span>
<span class="line">|Zusage/Jahr:    /        Konto.....:             Refusé-Anzahl:               |</span>
<span class="line">|Umsatz/Akq.: WAH         Kostenst..:             Responsanzahl:               |</span>
<span class="line">|Initialdat.: 15.04.1999  Kostentr..:             Respons in %.:               |</span>
<span class="line">|gilt bis...:             ESR-Nummer: 000000000   Responsewert.:               |</span>
<span class="line">+------------------------------------------------------------------------------+</span>
<span class="line">+------------------------------ Ereignis-Position -----------------------------+</span>
<span class="line">|EP Bezeichnung                    FDb Initial Refusé Respons  Res% Responswert|</span>
<span class="line">|00 ---                                      0      0       0              0.00|</span>
<span class="line">|01 beste Käufer     d                   80000      0       0  0.00        0.00|</span>
<span class="line">|02 gute Käufer      d                  160000      0       0  0.00        0.00|</span>
<span class="line">|03 schwache Käufer  d                  140000      0       0  0.00        0.00|</span>
<span class="line">|04 beste Käufer     f                   20000      0       0  0.00        0.00|</span>
<span class="line">etc.</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><p>Konto, Kostenstelle und Kostenträger lassen sich hier nicht eingeben, weil in der Kontierungsquellen-Tabelle definiert ist, dass diese aus dem Artikelstamm genommen werden (Annahme im Beispiel).Eine ESR-Nummer lässt sich hier nicht eingeben, weil es sich um ein Ereignis mit Situation 'RWV' handelt. Dieser Situationscode definiert das Ereignis als ein Verkaufsereignis und solchen Ereignissen wird in der Regel kein ESR beigelegt, denn der Zahlungseingang erfolgt aufgrund von Rechnungen mit integriertem Einzahlungsschein, die mit der Lieferung verschickt werden. (Wenn ein Katalog und ein Spendenaufruf kombiniert verschickt werden, sind 2 Ereignisse zu definieren.)</p>
<h4 id="_4-9-2-2-eintrag-in-der-ereignistabelle-spenden-aus-auftrag" tabindex="-1"><a class="header-anchor" href="#_4-9-2-2-eintrag-in-der-ereignistabelle-spenden-aus-auftrag"><span>4.9.2.2. Eintrag in der Ereignistabelle (Spenden aus Auftrag)</span></a></h4>
<p>Für die Zuordnung der &quot;Spenden aus Auftrag&quot; muss ein zweites Ereignis erfasst werden:</p>
<p>Pfad: <code v-pre>Adressen → Ereignisse</code></p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">+------------------------------- Ereignis-Kopf --------------------------------+</span>
<span class="line">|Ereignis.: 7777 Spenden aus Verkaufsaktionen 1999                             |</span>
<span class="line">|Situation: RSS  Spende/Aktion                                                 |</span>
<span class="line">|Proj/Code: Spenden/K                                                          |</span>
<span class="line">|aktiv......: ja          Mand./MWST: X/          Initialanzahl: 0             |</span>
<span class="line">|Zusage/Jahr:    /        Konto.....: 3015        Refusé-Anzahl: 0             |</span>
<span class="line">|Umsatz/Akq.: SPE         Kostenst..: 89100       Responsanzahl: 5,493         |</span>
<span class="line">|Initialdat.:             Kostentr..:             Respons in %.:               |</span>
<span class="line">|gilt bis...:             ESR-Nummer: 010232833   Responsewert.: 85,090.10     |</span>
<span class="line">+------------------------------------------------------------------------------+</span>
<span class="line">+----------------------------- Ereignis-Position ------------------------------+</span>
<span class="line">|EP Bezeichnung                    FDb Initial Refusé Respons  Res% Responswert|</span>
<span class="line">|00 ---                                      0      0       0              0.00|</span>
<span class="line">|01 Kampagne 1'99                            0      0    5471          83612.10|</span>
<span class="line">|02 Laden Luzern                             0      0       8             73.00|</span>
<span class="line">|14 Verkaufsaktion Abc                       0      0       0              0.00|</span>
<span class="line">etc.</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><h4 id="_4-9-2-3-eintrag-in-der-medientabelle" tabindex="-1"><a class="header-anchor" href="#_4-9-2-3-eintrag-in-der-medientabelle"><span>4.9.2.3. Eintrag in der Medientabelle</span></a></h4>
<p>Pfad: <code v-pre>Logistik → Tabellen → Medien</code></p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">+---------------------------------- Medien ------------------------------------+</span>
<span class="line">| Medium ErWV EP Bezeichnung                                                   |</span>
<span class="line">|        ErSP EP Konto        Kostenstelle Kostenträger erlauben               |</span>
<span class="line">|------------------------------------------------------------------------------|</span>
<span class="line">| 24     9876    Katalog Verkaufsaktion Abc                                    |</span>
<span class="line">|        7777.14                                        ja                     |</span>
<span class="line">| 25     9876    Flyer Verkaufsaktion Abc                                      |</span>
<span class="line">|        7777.14                                        ja                     |</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><p>Die Spenden aus Aufträgen, bei denen Medium 24 oder 25 in der ersten Auftragsposition eingegeben wird, bewirken nun beim jeweiligen Auftraggeber eine Ereignisverknüpfung mit Nr. 7777.14.</p>
<p>Die Warenhandelsumsätze werden auf Ereignis 9876 gebucht, der Response entsprechend den Initialereignissen auf eine bestimmte Ereignisposition verbucht.</p>
<p>Wenn gewünscht ist, dass auch Verkäufe zur Zeit der Verkaufsaktion Abc, die nicht aus den Medien 24 und 25 erfolgen, auf Ereignis 9876 gebucht werden, ist bei den verwendeten Auftragsarten (Auftragsarten-Tabelle) ebenfalls das Ereignis 9876 einzutragen. Denn wenn bei der Auftragserfassung keine Mediennummer in der jeweiligen Auftragsposition eingegeben wird, kann funtrade den Auftrag keinem Medium zuordnen, und die Ereignisnummer wird aus der Tabelle &quot;Auftragsarten&quot; genommen:</p>
<h4 id="_4-9-2-4-eintrag-in-der-tabelle-auftragsarten" tabindex="-1"><a class="header-anchor" href="#_4-9-2-4-eintrag-in-der-tabelle-auftragsarten"><span>4.9.2.4. Eintrag in der Tabelle Auftragsarten</span></a></h4>
<p>Pfad: `Verkauf → Tabellen → Auftragsarten``</p>
<div class="language-text line-numbers-mode" data-highlighter="prismjs" data-ext="text"><pre v-pre><code><span class="line">+------------------------------- Auftragsarten -------------------------------+</span>
<span class="line">| Code Mandant Steuercode Auftragsart                              ESRnr      |</span>
<span class="line">|-----------------------------------------------------------------------------|</span>
<span class="line">| dm   X       N          Direct Mail                              010232849  |</span>
<span class="line">|                                                                             |</span>
<span class="line">| Abstand....: 1          MWSt/TVA/IVA-Nr.: 234234                            |</span>
<span class="line">| Sortierung.: A                                                              |</span>
<span class="line">| Porto......: ja                                                             |</span>
<span class="line">| Order......: nein                                                           |</span>
<span class="line">| FreitextNr.: 0                                                              |</span>
<span class="line">| Umsatztyp..: wah                                                            |</span>
<span class="line">| WVerkUmsatz: nein                                                           |</span>
<span class="line">| Lagerort...: 96,909,919                                                     |</span>
<span class="line">| Kto/Kst/Ktr:                                 Lagerreservation..: normal     |</span>
<span class="line">| AdresMerkm.:            Formular...: 1       ext. Paketverarb..: ja         |</span>
<span class="line">| EreigSp/Zus: 7777 14    Dauerauftr.: n   /   vorausfakturierbar: nein       |</span>
<span class="line">| Ereignis-WV: 9876       Samm.Auftr.: nein    Druckername.......:            |</span>
<span class="line">| Frist......: 0          Samm.Faktur: nein                                   |</span>
<span class="line">| ZahlFrist..: 20         Sofortfakt.: nein Kto/Kst:                          |</span>
<span class="line">+-----------------------------------------------------------------------------+</span>
<span class="line"></span></code></pre>
<div class="line-numbers" aria-hidden="true" style="counter-reset:line-number 0"><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div><div class="line-number"></div></div></div><p>Wird nun bei der Auftragserfassung die Eingabe des Mediums nicht gemacht (z.B. vergessen), erstellt funtrade folgende Verknüpfungen (definiert in der Auftragsart):</p>
<ul>
<li>9876.00 im Verkaufsereignis (Umsatz Warenhandel)</li>
<li>7777.14 im Spendenereignis (im Auftragskopf erfasste Spende aus Auftrag)</li>
</ul>
<h2 id="_4-10-ereigniskosten" tabindex="-1"><a class="header-anchor" href="#_4-10-ereigniskosten"><span>4.10. Ereigniskosten</span></a></h2>
<p>Für die Beurteilung der Wirtschaftlichkeit von Kampagnen, sowie von Einzeladressen, ist es erforderlich, dass die Kosten von Ereignissen den Einnahmen gegenübergestellt werden können.</p>
<p>Für die Erfassung und Abbildung der Ereigniskosten stehen 2 Varianten zur Auswahl, die auch kombiniert werden können:</p>
<ul>
<li>Ereigniskostenstruktur: Erfassung/Anzeige basierend auf einer einzelnen Ereigniskostenstruktur.</li>
<li>Direkte Ereigniskostenerfassung: Erfassung von Rechnungen mit Kontendaten. Hier werden die Kosten nach Konto/Kst/Ktr angezeigt.</li>
</ul>
<p>Unabhängig von der Wahl der Variante werden die Kosten nach einem Aufteilungs-Schlüssel gemäss Tabelle Ereigniskostenaufteilung verteilt/vererbt und bei den einzelnen Ereignissen angezeigt. Die kumulierten Kosten der einzelnen Ereignisse werden auch in den Projekten angezeigt.</p>
<h3 id="_4-10-1-ablauf" tabindex="-1"><a class="header-anchor" href="#_4-10-1-ablauf"><span>4.10.1. Ablauf</span></a></h3>
<ol>
<li>Wahl der Variante: Ereigniskostenstruktur oder &quot;direkte Erfassung&quot;?<br></li>
</ol>
<ul>
<li>Falls die Variante &quot;direkte Erfassung&quot; gewählt wird, weiter mit Punkt 3.</li>
</ul>
<ol start="2">
<li>Erfassung der Ereigniskostenstruktur.</li>
<li>Erfassung der Kosten durch Eintrag der einzelnen Kostenpositionen in der Tabelle Ereigniskostenerfassung.</li>
<li>Aufteilung der Ereigniskosten auf die einzelnen Ereignishierarchien/Ereignisse in der Tabelle Ereigniskostenaufteilung.</li>
</ol>
<h2 id="_4-11-ereignis-weiterleitung" tabindex="-1"><a class="header-anchor" href="#_4-11-ereignis-weiterleitung"><span>4.11. Ereignis Weiterleitung</span></a></h2>
<p>Zahlungseingänge auf ein Ereignis können automatisch auf ein anderes Ereignis weitergeleitet werden. Praktische Beispiele sind z.B. Spendenzahlungen mit einem alten Einzahlungsschein, die zwischenzeitlich auf ein anderes, resp. neues Ereignis zu verbuchen sind, oder zuviel bezahlte Beiträge bei Zusagen, wo man den Rest automatisch als Spende verbuchen möchte.</p>
<p>Details zur Einrichtung und Parametrisierung, siehe</p>
<ul>
<li>Ereignis Weiterleitung</li>
</ul>
</div></template>


